import { useState, useEffect } from 'react';
import { StatusService } from '../types/services/StatusService';

interface NavbarCounts {
    receivedPackages: number;
    pendingShipments: number;
    loading: boolean;
    error: string | null;
}

export const useNavbarCounts = () => {
    const [counts, setCounts] = useState<NavbarCounts>({
        receivedPackages: 0,
        pendingShipments: 0,
        loading: true,
        error: null,
    });

    const fetchCounts = async () => {
        try {
            setCounts(prev => ({ ...prev, loading: true, error: null }));

            // Fetch packages that are received but not shipped (isShipped: false)
            const packageResponse = await StatusService.getStatusPackages(
                undefined, undefined, undefined, false, false, false
            );

            // Fetch shipments that are requested but not delivered yet (state: REQUESTED)
            const shipmentResponse = await StatusService.getStatusShipments(
                undefined, // startDate
                undefined, // endDate
                'APPROVED' // status - get shipments that are requested but not approved/delivered
            );

            const receivedPackages = packageResponse.data?.total || 0;
            const pendingShipments = shipmentResponse.data?.byState?.REQUESTED || 0;

            setCounts({
                receivedPackages,
                pendingShipments,
                loading: false,
                error: null,
            });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to fetch counts';
            setCounts(prev => ({
                ...prev,
                loading: false,
                error: errorMessage,
            }));

            // Provide user feedback via notifications
            import('../utils/errorHandling').then(({ handleApiError }) => {
                handleApiError(error, 'Navigation Error', 'Failed to load navigation counts. Please refresh the page.');
            });
        }
    };

    useEffect(() => {
        fetchCounts();
    }, []);

    return { ...counts, refetch: fetchCounts };
};
